import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";
import colors from "assets/theme/base/colors";
import { UserContext, notify } from "App";
import { GetDevoteeDataAPI, GetDevoteeAddressAPI } from "APIs/DevoteeAPIs";
import SectionCard, {
  SectionHeader,
  ranuOutlinedButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";
import FormField from "layouts/receipt/components/FormField";

const goldHoverSx = ({ palette }) => ({
  "&:hover": {
    backgroundColor: palette.secondary.main,
    borderColor: palette.secondary.main,
    color: palette.white.main,
    boxShadow: "none",
  },
});

const DEVOTEE_FIELDS = [
  "first_name",
  "last_name",
  "anniversary_date",
  "birth_date",
  "email",
  "gender",
  "middle_name",
  "phone",
  ,
];

const ADDRESS_FIELDS = [
  "address_line_1",
  "address_line_2",
  "area",
  "city",
  "country",
  "landmark",
  "phone",
  "pincode",
  "state",
  "title",
];

const FULL_WIDTH_FIELDS = ["address_line_1", "address_line_2"];

const ViewDevotee = () => {
  const [devoteeData, setDevoteeData] = useState({});
  const [devoteeAddress, setDevoteeAddress] = useState([]);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const userData = useContext(UserContext);
  const navigate = useNavigate();
  const { accentName } = useAccent();
  const { devotee_id } = useParams();

  const GetDevoteeDataAPIFunc = async (devotee_id, access_token) => {
    const response = await GetDevoteeDataAPI(devotee_id, access_token);
    if (response?.is_success) {
      setDevoteeData({
        ...response.data,
        gender: response.data.gender == 0 ? "Male" : "Female",
      });
    } else {
      notify(response?.message);
    }
  };

  const GetDevoteeAddressFunc = async (devotee_id, access_token) => {
    setIsFetchingData(true);
    const response = await GetDevoteeAddressAPI(devotee_id, access_token);
    if (response.is_success) {
      setDevoteeAddress(response.data.list);
    }
    setIsFetchingData(false);
  };

  useEffect(() => {
    if (userData.access_token) {
      GetDevoteeDataAPIFunc(devotee_id, userData.access_token);
    }
  }, [userData]);

  useEffect(() => {
    if (userData?.access_token && devoteeData?.devotee_id) {
      GetDevoteeAddressFunc(devoteeData.devotee_id, userData.access_token);
    }
  }, [devoteeData, userData]);

  return (
    <MDBox px={{ xs: 1, md: 3 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <SectionHeader
        icon="visibility"
        label="VIEW DEVOTEE"
        startAction={
          <MDButton
            variant="outlined"
            color={accentName}
            size="medium"
            sx={ranuOutlinedButtonSx}
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/devotee")}
          >
            Back
          </MDButton>
        }
        endAction={
          <MDBox
            sx={{
              display: { xs: "none", md: "block" },
            }}
          >
            <MDButton
              color="primary"
              size="medium"
              sx={[ranuOutlinedButtonSx, goldHoverSx]}
              startIcon={<EditIcon />}
              onClick={() => navigate(`/devotee/edit/${devotee_id}`)}
            >
              Edit Devotee
            </MDButton>
          </MDBox>
        }
      />

      {/* ------------------------------ Details ------------------------------ */}
      <SectionCard title="Details">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems="center"
              gap={4}
              mb={1}
              sx={{ textAlign: { xs: "center", sm: "left" } }}
            >
              {/* Same avatar as the list: shows the default person icon when there is no picture */}
              <MDAvatar
                bgColor="primary"
                src={
                  devoteeData.picture && devoteeData.picture[devoteeData.picture.length - 1] !== "/"
                    ? devoteeData.picture
                    : undefined
                }
                alt="Avatar"
                sx={{
                  width: "170px",
                  height: "170px",
                  border: `3px solid ${colors[accentName].main}`,
                }}
              />

              <div>
                <MDTypography variant="h4" color={accentName}>
                  {`${devoteeData?.first_name ?? ""} ${devoteeData?.last_name ?? ""}`.trim()}
                </MDTypography>

                {devoteeData.email && (
                  <MDTypography variant="button" color="text" display="block">
                    <a href={`mailto:${devoteeData.email}`} style={{ color: "#000000" }}>
                      {devoteeData.email}
                    </a>
                  </MDTypography>
                )}
              </div>
            </MDBox>
          </Grid>

          {Object.keys(devoteeData)
            .filter((items) => DEVOTEE_FIELDS.includes(items))
            .map((items) => (
              <FormField key={items} name={items}>
                <MDInput disabled fullWidth value={devoteeData[items] ? devoteeData[items] : ""} />
              </FormField>
            ))}
        </Grid>
      </SectionCard>

      {/* ----------------------------- Addresses ----------------------------- */}
      {devoteeAddress?.length > 0 && (
        <SectionCard title="Addresses">
          {devoteeAddress.map((addresses, key) => (
            <MDBox
              key={key}
              mb={2}
              p={{ xs: 1.5, md: 2 }}
              sx={({ borders }) => ({
                border: `${borders.borderWidth[1]} solid ${borders.borderColor}`,
                borderRadius: borders.borderRadius.lg,
              })}
            >
              <MDTypography
                variant="button"
                fontWeight="bold"
                color={accentName}
                display="block"
                mb={2}
              >
                Address {key + 1}
              </MDTypography>

              <Grid container spacing={2}>
                {Object.keys(addresses)
                  .filter((items) => ADDRESS_FIELDS.includes(items))
                  .map((items) => (
                    <FormField key={items} name={items} full={FULL_WIDTH_FIELDS.includes(items)}>
                      <MDInput disabled fullWidth value={addresses[items]} />
                    </FormField>
                  ))}
              </Grid>
            </MDBox>
          ))}
        </SectionCard>
      )}

      {/* --------------------------- Mobile Edit --------------------------- */}
      <MDBox
        sx={{
          display: { xs: "flex", md: "none" },
          justifyContent: "center",
          mt: 2,
        }}
      >
        <MDButton
          color="primary"
          size="medium"
          sx={[ranuOutlinedButtonSx, goldHoverSx]}
          startIcon={<EditIcon />}
          onClick={() => navigate(`/devotee/edit/${devotee_id}`)}
        >
          Edit Devotee
        </MDButton>
      </MDBox>
    </MDBox>
  );
};

export default ViewDevotee;
